import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { FiCheck, FiChevronDown, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import websocketTaskService from "../../../services/websocketTaskService";
import { tokens as T, hairline } from "../../../styles/designTokens";
import { tokens as activity } from "../../activity-logs/styles";
import { SANS } from "../../creditos/styles";
import { Capsule, PrimaryCapsule, RoundButton } from "../../creditos/components/ControlBar";
import { MONTHS, branchLabel, formatCnpj, formatCount, formatMonthKey } from "./revisorData";

/**
 * Nova análise do Revisor SPED.
 * A pessoa só solta os SPEDs: cada arquivo é lido na hora (registro 0000) e a
 * análise se monta sozinha — período, empresas e o que falta. Nada a digitar.
 *
 * Visual: superfícies neutras, cor só onde há significado (verde no botão
 * principal e nas confirmações, laranja discreto no que falta).
 * API: POST /revisor/upload por arquivo e POST /revisor/process, como antes;
 * as etapas do servidor chegam pelo websocketTaskService.
 *
 * Com `completion`, completa uma análise: aceita só os SPEDs que faltam e
 * reprocessa o período. O servidor guarda os SPEDs já enviados
 * (GET /revisor/uploads), então eles não são enviados de novo.
 */

const API_URL = process.env.REACT_APP_API || "https://api.vidal-app.com";
const ORANGE = "#ff9f0a";
const RED = "#ff453a";

const STEPS = [
  { id: 0, name: "Envio dos arquivos" },
  { id: 1, name: "Validação" },
  { id: 2, name: "Empresas e participantes" },
  { id: 3, name: "Notas fiscais" },
  { id: 4, name: "Conhecimentos de transporte" },
  { id: 5, name: "Conferência com os XMLs" },
  { id: 6, name: "Indexação do relatório" },
  { id: 7, name: "Indexação dos fornecedores" },
  { id: 8, name: "Indexação das empresas" },
  { id: 9, name: "Pacote para download" },
];

// ─── Leitura do SPED ─────────────────────────────────────────────────────────

const readHeader = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(String(e.target.result || ""));
  reader.onerror = reject;
  reader.readAsText(file.slice(0, 4096), "latin1");
});

const isDate = (s) => /^\d{8}$/.test(s || "");
const monthOf = (ddmmyyyy) => `${ddmmyyyy.slice(4, 8)}-${ddmmyyyy.slice(2, 4)}`;

// Registro 0000 da EFD ICMS/IPI: |0000|COD_VER|COD_FIN|DT_INI|DT_FIN|NOME|CNPJ|CPF|UF|...
export function parseSped(text, fileName) {
  const f = (text.split(/\r?\n/)[0] || "").split("|");
  if (f[1] !== "0000") return { error: "Não é um SPED" };
  // EFD Contribuições: |0000|COD_VER|TIPO_ESCRIT|IND_SIT_ESP|NUM_REC|DT_INI|DT_FIN|NOME|CNPJ|...
  if (!isDate(f[4]) && isDate(f[6]) && isDate(f[7])) return { error: "É EFD Contribuições; use a EFD ICMS/IPI" };
  if (!isDate(f[4]) || !isDate(f[5])) return { error: "Período ilegível" };
  const cnpj = String(f[7] || "").replace(/\D/g, "");
  if (cnpj.length !== 14) return { error: "CNPJ ilegível" };
  const start = monthOf(f[4]);
  const end = monthOf(f[5]);
  if (start !== end) return { error: "Cobre mais de um mês" };
  return { cnpj, month: start, company: (f[6] || "").trim() || fileName, uf: (f[9] || "").trim() };
}

const monthIndex = (key) => { const [y, m] = key.split("-").map(Number); return y * 12 + (m - 1); };
const keyOfIndex = (i) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;
const lastDay = (key) => { const [y, m] = key.split("-").map(Number); return new Date(y, m, 0).getDate(); };
const brDate = (key, day) => `${String(day).padStart(2, "0")}/${key.slice(5)}/${key.slice(0, 4)}`;
const shortMonth = (key) => MONTHS[Number(key.slice(5)) - 1];

function buildPlan(files) {
  const slots = new Map();
  const marked = files.map((f) => {
    if (f.status !== "ok") return f;
    const slot = `${f.cnpj}|${f.month}`;
    if (slots.has(slot)) return { ...f, dup: true };
    slots.set(slot, f.id);
    return { ...f, dup: false };
  });
  const usable = marked.filter((f) => f.status === "ok" && !f.dup);
  const ignored = marked.filter((f) => f.status === "bad" || f.dup);
  if (usable.length === 0) return { usable, ignored, months: [], companies: [], missing: [] };

  const keys = usable.map((f) => f.month).sort();
  const months = [];
  for (let i = monthIndex(keys[0]); i <= monthIndex(keys[keys.length - 1]); i += 1) months.push(keyOfIndex(i));

  const byCnpj = new Map();
  usable.forEach((f) => {
    if (!byCnpj.has(f.cnpj)) byCnpj.set(f.cnpj, { cnpj: f.cnpj, company: f.company, files: new Map() });
    byCnpj.get(f.cnpj).files.set(f.month, f);
  });
  const companies = [...byCnpj.values()].sort((a, b) => a.cnpj.localeCompare(b.cnpj));
  const missing = [];
  companies.forEach((c) => months.forEach((m) => { if (!c.files.has(m)) missing.push({ cnpj: c.cnpj, month: m }); }));
  return { usable, ignored, months, companies, missing };
}

// ─── Estilos ─────────────────────────────────────────────────────────────────

const dk = (theme) => activity.dk(theme);
const label2 = ({ theme }) => activity.label2(theme);
const label3 = ({ theme }) => activity.label3(theme);
const sheetBg = ({ theme }) => (dk(theme) ? "#1c1c1e" : "#f2f2f7");
const cardBg = ({ theme }) => (dk(theme) ? "#2c2c2e" : "#ffffff");
const fill = ({ theme }) => (dk(theme) ? "rgba(118,118,128,0.24)" : "rgba(118,118,128,0.12)");
const sep = ({ theme }) => hairline(theme, true);

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const rise = keyframes`from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: none; }`;
const enter = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const float = keyframes`0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, ${({ theme }) => (dk(theme) ? 0.5 : 0.28)});
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  animation: ${fadeIn} 200ms ease;
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;
  -webkit-font-smoothing: antialiased;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Sheet = styled.div`
  width: min(560px, 100%);
  max-height: min(720px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 26px;
  background: ${sheetBg};
  color: ${({ theme }) => theme.text};
  box-shadow: 0 0 0 0.5px ${sep}, 0 50px 100px -30px rgba(0, 0, 0, 0.5), 0 8px 24px -8px rgba(0, 0, 0, 0.18);
  animation: ${rise} 320ms cubic-bezier(0.2, 0.9, 0.25, 1);
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

// Cabeçalho mínimo, como as folhas do iOS: título à esquerda, fechar à direita
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 18px 6px 22px;
  h2 { flex: 1; margin: 0; font-size: 1.12em; font-weight: 700; letter-spacing: -0.025em; }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 22px 22px;
  overscroll-behavior: contain;
  animation: ${enter} 260ms ease-out;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Lead = styled.p`
  margin: 0 0 18px;
  font-size: 0.86em;
  line-height: 1.5;
  color: ${label2};
  b { color: ${({ theme }) => theme.text}; font-weight: 500; }
`;

// Área de soltar: neutra; só a borda e o ícone reagem ao arrastar
const Drop = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  border-radius: 20px;
  background: ${cardBg};
  box-shadow: inset 0 0 0 1.5px ${({ $over }) => ($over ? T.brand : "transparent")};
  transition: box-shadow 180ms ease, transform 220ms cubic-bezier(0.2, 0.9, 0.3, 1.2), background-color 180ms ease;
  ${({ $over }) => $over && css`transform: scale(1.008);`}
  input { display: none; }

  ${({ $compact }) => ($compact ? css`
    flex-direction: row;
    justify-content: flex-start;
    gap: 12px;
    padding: 12px 14px;
    text-align: left;
    border-radius: 16px;
  ` : css`
    padding: 40px 24px 30px;
  `)}

  .docs { position: relative; width: 58px; height: 64px; margin-bottom: 18px; animation: ${({ $over }) => ($over ? css`${float} 1s ease-in-out infinite` : "none")}; }
  .docs i {
    position: absolute;
    width: 44px;
    height: 56px;
    border-radius: 9px;
    background: ${cardBg};
    box-shadow: 0 0 0 1px ${sep}, 0 4px 10px -4px rgba(0, 0, 0, 0.18);
    transition: transform 260ms cubic-bezier(0.2, 0.9, 0.3, 1.2);
  }
  .docs i:nth-child(1) { left: 0; top: 6px; transform: rotate(-9deg); }
  .docs i:nth-child(2) { left: 14px; top: 4px; transform: rotate(8deg); }
  .docs i:nth-child(3) {
    left: 7px; top: 0;
    display: flex; flex-direction: column; justify-content: center; gap: 5px; padding: 0 9px;
  }
  .docs i:nth-child(3) span { height: 3px; border-radius: 2px; background: ${fill}; }
  .docs i:nth-child(3) span:first-child { width: 60%; background: ${({ $over }) => ($over ? T.brand : label3)}; }
  &:hover .docs i:nth-child(1) { transform: rotate(-13deg) translateX(-2px); }
  &:hover .docs i:nth-child(2) { transform: rotate(12deg) translateX(2px); }

  .t { font-size: 1.02em; font-weight: 600; letter-spacing: -0.02em; }
  .s { margin-top: 4px; font-size: 0.8em; color: ${label2}; }
  .btn { margin-top: 18px; pointer-events: none; }
  .plus {
    width: 30px; height: 30px; flex-shrink: 0; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 16px; font-weight: 400; color: ${label2}; background: ${fill};
  }
  .ct { font-size: 0.86em; font-weight: 500; }
  .cs { font-size: 0.74em; color: ${label2}; }
`;

// "Como funciona" em uma linha, para a tela vazia se explicar sozinha
const HowTo = styled.ol`
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  justify-content: center;
  gap: 18px;
  flex-wrap: wrap;
  font-size: 0.76em;
  color: ${label2};
  li { display: inline-flex; align-items: center; gap: 7px; }
  li b {
    width: 18px; height: 18px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.9em; font-weight: 600; color: ${({ theme }) => theme.text}; background: ${fill};
  }
`;

const Hint = styled.div`
  margin: 0 0 14px;
  font-size: 0.8em;
  color: ${label2};
  b { color: ${({ theme }) => theme.text}; font-weight: 500; }
`;

const Section = styled.div`
  margin-top: 22px;
  .lbl {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin: 0 4px 8px;
    font-size: 0.78em;
    font-weight: 500;
    color: ${label2};
  }
`;

const Card = styled.div`
  border-radius: 16px;
  overflow: hidden;
  background: ${cardBg};
`;

// Período: o resultado da leitura, em destaque
const Period = styled.div`
  padding: 16px 16px 15px;
  display: flex;
  align-items: center;
  gap: 14px;
  .p { flex: 1; min-width: 0; }
  .big { font-size: 1.4em; font-weight: 700; letter-spacing: -0.03em; line-height: 1.15; }
  .sub { margin-top: 3px; font-size: 0.8em; color: ${label2}; }
  .st { display: inline-flex; align-items: center; gap: 6px; font-size: 0.78em; font-weight: 500; color: ${label2}; white-space: nowrap; }
  .st i { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
`;

const Row = styled.div`
  position: relative;
  min-height: 56px;
  padding: 10px 10px 10px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${enter} 240ms ease-out both;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: ${({ $inset }) => $inset || 60}px;
    right: 0;
    height: 0.5px;
    background: ${sep};
  }
  &:first-child::before { display: none; }

  .av {
    width: 32px; height: 32px; flex-shrink: 0; border-radius: 9px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.68em; font-weight: 600; letter-spacing: 0.01em;
    color: ${label2}; background: ${fill};
  }
  .main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .t { font-size: 0.88em; font-weight: 500; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .s { font-size: 0.74em; color: ${label2}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .x { opacity: 0; transition: opacity 140ms ease; }
  &:hover .x, .x:focus-visible { opacity: 1; }
  @media (hover: none) { .x { opacity: 1; } }
`;

// Meses de cada empresa: pílulas neutras; a que falta tem contorno laranja
const Months = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: flex-end;
  span {
    height: 24px;
    padding: 0 8px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.72em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    background: ${fill};
  }
  span svg { color: ${T.brand}; stroke-width: 3.2; font-size: 11px; }
  span.miss { color: ${ORANGE}; background: transparent; box-shadow: inset 0 0 0 1px rgba(255, 159, 10, 0.55); }
  span.off { box-shadow: inset 0 0 0 1px ${sep}; background: transparent; }
`;

const Disclosure = styled.button`
  width: 100%;
  min-height: 46px;
  padding: 0 16px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: inherit;
  font-size: 0.84em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  text-align: left;
  .dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .n { color: ${label2}; font-weight: 400; }
  .chev { margin-left: auto; color: ${label3}; transition: transform 220ms ease; transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg); }
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px 18px 22px;
  border-top: 0.5px solid ${sep};
  .msg { flex: 1; min-width: 0; display: flex; align-items: center; gap: 8px; font-size: 0.8em; color: ${label2}; }
  .msg i { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .msg span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .msg b { color: ${({ theme }) => theme.text}; font-weight: 500; }
  @media (max-width: 480px) {
    flex-wrap: wrap;
    .msg { flex-basis: 100%; }
    button { flex: 1; }
  }
`;

// ─── Processamento ───────────────────────────────────────────────────────────

const Progress = styled.div`
  padding: 22px 0 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  .big { margin-top: 16px; font-size: 1.15em; font-weight: 700; letter-spacing: -0.025em; }
  .sub { margin-top: 4px; font-size: 0.82em; color: ${label2}; min-height: 1.2em; }
`;

function Ring({ value, state }) {
  const r = 34;
  const c = 2 * Math.PI * r;
  const stroke = state === "error" ? RED : T.brand;
  let center = `${Math.round(value)}%`;
  if (state === "done") center = <FiCheck style={{ fontSize: 28, strokeWidth: 2.6, color: T.brand }} />;
  if (state === "error") center = <FiX style={{ fontSize: 28, strokeWidth: 2.6, color: RED }} />;
  return (
    <div style={{ position: "relative", width: 84, height: 84 }}>
      <svg width="84" height="84" viewBox="0 0 84 84" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="42" cy="42" r={r} fill="none" stroke="rgba(118,118,128,0.18)" strokeWidth="5" />
        <circle
          cx="42"
          cy="42"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.min(100, value) / 100)}
          style={{ transition: "stroke-dashoffset 600ms cubic-bezier(0.2, 0.8, 0.2, 1)" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 16, letterSpacing: "-0.02em" }}>
        {center}
      </div>
    </div>
  );
}

const Step = styled.div`
  position: relative;
  min-height: 40px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.84em;
  &::before { content: ""; position: absolute; top: 0; left: 46px; right: 0; height: 0.5px; background: ${sep}; }
  &:first-child::before { display: none; }
  .d {
    width: 18px; height: 18px; flex-shrink: 0; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center; font-size: 11px;
  }
  .d.pending { box-shadow: inset 0 0 0 1.5px ${sep}; }
  .d.running { border: 2px solid ${fill}; border-top-color: ${T.brand}; animation: ${spin} 800ms linear infinite; }
  .d.completed { color: ${T.brand}; }
  .d.completed svg { stroke-width: 3.4; }
  .d.error { color: ${RED}; }
  .d.error svg { stroke-width: 3.4; }
  .nm { flex: 1; color: ${({ $s, theme }) => ($s === "pending" ? activity.label3(theme) : $s === "completed" ? activity.label2(theme) : theme.text)}; font-weight: ${({ $s }) => ($s === "running" ? 600 : 400)}; }
  .pc { font-size: 0.9em; color: ${label2}; }
`;

const Logs = styled.div`
  max-height: 160px;
  overflow-y: auto;
  padding: 2px 16px 14px;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.7em;
  line-height: 1.65;
  color: ${label2};
  .e { color: ${RED}; }
  .ts { opacity: 0.55; margin-right: 8px; }
`;

const Spinner = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(118, 118, 128, 0.3);
  border-top-color: ${T.brand};
  animation: ${spin} 800ms linear infinite;
`;

// ─── Componente ──────────────────────────────────────────────────────────────

let fileSeq = 0;
// Número do estabelecimento (0001 → 1, 0003 → 3), o que diferencia as linhas
const branchNo = (cnpj) => String(Number(String(cnpj).slice(8, 12)) || "?");

export default function NovaAnaliseSheet({ isOpen, onClose, targetMonth, analyzedMonths, onStart, onFinished, completion }) {
  const [phase, setPhase] = useState("files");
  const [files, setFiles] = useState([]);
  const [over, setOver] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [runState, setRunState] = useState("idle");
  const [jobId, setJobId] = useState(null);
  const [reportId, setReportId] = useState(null);
  const dragDepth = useRef(0);
  const prevStates = useRef({});

  useEffect(() => {
    if (!isOpen) return;
    setPhase("files");
    setFiles([]);
    setTasks([]);
    setLogs([]);
    setRunState("idle");
    setJobId(null);
    setReportId(null);
    setShowIgnored(false);
    setShowLogs(false);
    prevStates.current = {};
  }, [isOpen]);

  const plan = useMemo(() => {
    if (!completion) return buildPlan(files);
    const wanted = new Set(completion.missing.map((m) => `${m.cnpj}|${m.month}`));
    return buildPlan(files.map((f) => (f.status === "ok" && !wanted.has(`${f.cnpj}|${f.month}`)
      ? { ...f, status: "bad", reason: `${branchLabel(f.cnpj)} em ${shortMonth(f.month)} não está entre os SPEDs que faltam` }
      : f)));
  }, [files, completion]);
  const reading = files.filter((f) => f.status === "reading").length;
  const canProcess = plan.usable.length > 0 && reading === 0 && (!!completion || plan.missing.length === 0);

  const log = useCallback((type, message) => {
    setLogs((prev) => [{ type, message, ts: new Date().toLocaleTimeString("pt-BR") }, ...prev]);
  }, []);

  const addFiles = useCallback(async (list) => {
    const incoming = Array.from(list || []).map((file) => ({ id: (fileSeq += 1), file, name: file.name, size: file.size, status: "reading" }));
    if (incoming.length === 0) return;
    setFiles((prev) => [...prev, ...incoming]);
    await Promise.all(incoming.map(async (item) => {
      let patch;
      if (!/\.txt$/i.test(item.name)) patch = { status: "bad", reason: "Não é um arquivo .txt" };
      else {
        try {
          const info = parseSped(await readHeader(item.file), item.name);
          patch = info.error ? { status: "bad", reason: info.error } : { status: "ok", ...info };
        } catch {
          patch = { status: "bad", reason: "Não foi possível ler" };
        }
      }
      setFiles((prev) => prev.map((f) => (f.id === item.id ? { ...f, ...patch } : f)));
    }));
  }, []);

  const removeIds = (ids) => setFiles((prev) => prev.filter((f) => !ids.includes(f.id)));

  const dragHandlers = {
    onDragEnter: (e) => { e.preventDefault(); dragDepth.current += 1; setOver(true); },
    onDragLeave: (e) => { e.preventDefault(); dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setOver(false); } },
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => { e.preventDefault(); dragDepth.current = 0; setOver(false); addFiles(e.dataTransfer.files); },
  };
  const picker = <input type="file" multiple accept=".txt" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />;

  // ── Processar ──
  const process = async () => {
    const first = completion ? completion.startKey : plan.months[0];
    const last = completion ? completion.endKey : plan.months[plan.months.length - 1];
    const startDate = brDate(first, 1);
    const endDate = brDate(last, lastDay(last));
    const cnpjs = completion
      ? [...new Set([...completion.existing, ...plan.companies.map((c) => c.cnpj)])]
      : plan.companies.map((c) => c.cnpj);

    onStart?.();
    setPhase("processing");
    setRunState("running");
    setLogs([]);
    prevStates.current = {};
    setTasks(STEPS.map((s) => ({ ...s, progress: 0, status: s.id === 0 ? "running" : "pending" })));
    log("info", `Período ${startDate} a ${endDate} · ${cnpjs.length} empresas · ${plan.usable.length} arquivos`);

    try {
      const auth = JSON.parse(localStorage.getItem("vidal_auth") || "{}");
      const projectId = localStorage.getItem("vidal_selected_project");
      if (!projectId) throw new Error("Nenhum projeto selecionado");
      const headers = { Authorization: `Bearer ${auth.token}`, projeto: projectId, user: auth.user?.id };

      for (let i = 0; i < plan.usable.length; i += 1) {
        const f = plan.usable[i];
        const body = new FormData();
        body.append("file", f.file);
        body.append("projectId", projectId);
        body.append("companyCnpj", f.cnpj);
        body.append("year", Number(f.month.slice(0, 4)));
        body.append("month", Number(f.month.slice(5)));
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(`${API_URL}/revisor/upload`, { method: "POST", headers, body });
        if (!res.ok) throw new Error(`Falha ao enviar ${f.name}`);
        const progress = ((i + 1) / plan.usable.length) * 100;
        setTasks((prev) => prev.map((t) => (t.id === 0 ? { ...t, progress } : t)));
        log("info", `${f.name} enviado`);
      }
      setTasks((prev) => prev.map((t) => (t.id === 0 ? { ...t, progress: 100, status: "completed" } : t)));

      const res = await fetch(`${API_URL}/revisor/process`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, companyIds: cnpjs, startDate, endDate }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Não foi possível iniciar o processamento");
      setJobId(data.jobId);
      log("info", `Processamento iniciado (job ${data.jobId})`);
    } catch (err) {
      console.error("[Revisor] Nova análise:", err);
      setRunState("error");
      setTasks((prev) => prev.map((t) => (t.status === "running" ? { ...t, status: "error" } : t)));
      log("error", err.message);
      toast.error(err.message);
    }
  };

  useEffect(() => {
    if (runState !== "running" || !jobId) return undefined;
    const onTasks = (all) => {
      const ours = all.find((t) => t.operationId === jobId && t.type === "process_speds_revisor");
      if (!ours) return;
      if (Array.isArray(ours.tasks)) {
        ours.tasks.forEach((wt) => {
          const prev = prevStates.current[wt.id];
          if (wt.status === "completed" && prev !== "completed") log("info", `Concluído: ${wt.name}`);
          if (wt.status === "failed" && prev !== "failed") log("error", `Falhou: ${wt.name}${wt.error ? ` (${wt.error})` : ""}`);
          prevStates.current[wt.id] = wt.status;
        });
        setTasks((prev) => prev.map((t) => {
          if (t.id === 0) return t;
          const wt = ours.tasks.find((x) => x.id === t.id);
          return wt ? { ...t, status: wt.status === "failed" ? "error" : wt.status, progress: wt.progress || 0 } : t;
        }));
      }
      if (ours.status === "completed") {
        setRunState("done");
        setReportId(ours.reportId || null);
        setTasks((prev) => prev.map((t) => ({ ...t, status: "completed", progress: 100 })));
        log("info", "Análise concluída");
      }
      if (ours.status === "failed") {
        setRunState("error");
        log("error", ours.message || "Falha no processamento");
        toast.error(`Erro no processamento: ${ours.message || "falha desconhecida"}`);
      }
    };
    return websocketTaskService.subscribe(onTasks);
  }, [runState, jobId, log]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overall = tasks.length ? tasks.reduce((s, t) => s + (t.status === "completed" ? 100 : t.progress || 0), 0) / tasks.length : 0;
  const current = tasks.find((t) => t.status === "running");
  let periodText = "";
  if (plan.months.length === 1) periodText = formatMonthKey(plan.months[0], true);
  else if (plan.months.length > 1) periodText = `${formatMonthKey(plan.months[0])} – ${formatMonthKey(plan.months[plan.months.length - 1])}`;
  const repeated = plan.months.filter((m) => analyzedMonths?.has(m));
  const offTarget = targetMonth && plan.usable.some((f) => f.month !== targetMonth);

  // ── Conteúdo: arquivos ──
  let content;
  if (phase === "files" && completion) {
    const received = new Set(plan.usable.map((f) => `${f.cnpj}|${f.month}`));
    content = (
      <>
        <Lead>
          Envie os SPEDs que faltam em <b>{completion.label}</b>. O período será reprocessado com eles e com os {formatCount(completion.existing.length)} já enviados, que não precisam ser enviados de novo. O resultado será uma nova análise.
        </Lead>
        <Section>
          <div className="lbl"><span>Faltam</span><span>{formatCount(received.size)} de {formatCount(completion.missing.length)} recebidos</span></div>
          <Card>
            {completion.missing.map((m) => {
              const ok = received.has(`${m.cnpj}|${m.month}`);
              return (
                <Row key={`${m.cnpj}-${m.month}`}>
                  <span className="av">{ok ? <FiCheck style={{ color: T.brand }} /> : branchNo(m.cnpj)}</span>
                  <span className="main">
                    <span className="t">{branchLabel(m.cnpj)}</span>
                    <span className="s">{formatCnpj(m.cnpj)} · {formatMonthKey(m.month)}</span>
                  </span>
                  <Months>{ok ? <span><FiCheck />Recebido</span> : <span className="miss">Falta</span>}</Months>
                </Row>
              );
            })}
            {reading > 0 && (
              <Row>
                <span className="av"><Spinner /></span>
                <span className="main"><span className="t">Lendo {reading} {reading === 1 ? "arquivo" : "arquivos"}…</span></span>
              </Row>
            )}
          </Card>
        </Section>
        {plan.ignored.length > 0 && (
          <Section>
            <Card>
              <Disclosure type="button" $open={showIgnored} onClick={() => setShowIgnored((v) => !v)} aria-expanded={showIgnored}>
                <span className="dot" style={{ background: ORANGE }} />
                {plan.ignored.length} {plan.ignored.length === 1 ? "arquivo ignorado" : "arquivos ignorados"}
                <FiChevronDown className="chev" />
              </Disclosure>
              {showIgnored && plan.ignored.map((f) => (
                <Row key={f.id} $inset={16}>
                  <span className="main">
                    <span className="t" title={f.name}>{f.name}</span>
                    <span className="s">{f.dup ? `Repetido: ${branchLabel(f.cnpj)} em ${shortMonth(f.month)}` : f.reason}</span>
                  </span>
                  <RoundButton type="button" className="x" onClick={() => removeIds([f.id])} aria-label={`Remover ${f.name}`} title="Remover" style={{ width: 26, height: 26 }}>
                    <FiX />
                  </RoundButton>
                </Row>
              ))}
            </Card>
          </Section>
        )}
        <Section style={{ marginTop: 14 }}>
          <Drop $compact $over={over} {...dragHandlers}>
            {picker}
            <span className="plus">+</span>
            <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <span className="ct">{over ? "Solte para adicionar" : "Adicionar SPEDs"}</span>
              <span className="cs">Arraste aqui ou clique · EFD ICMS/IPI em .txt</span>
            </span>
          </Drop>
        </Section>
      </>
    );
  } else if (phase === "files" && files.length === 0) {
    content = (
      <>
        <Lead>
          Solte os SPEDs do período que quer conferir. O período e as empresas são lidos dos próprios arquivos.
          {targetMonth && <> Esperado: <b>{formatMonthKey(targetMonth, true)}</b>.</>}
        </Lead>
        <Drop $over={over} {...dragHandlers}>
          {picker}
          <span className="docs"><i /><i /><i><span /><span /><span /></i></span>
          <span className="t">{over ? "Solte para ler" : "Solte os arquivos aqui"}</span>
          <span className="s">EFD ICMS/IPI em .txt · um por estabelecimento e mês</span>
          <Capsule as="span" className="btn">Escolher arquivos</Capsule>
        </Drop>
        <HowTo>
          <li><b>1</b>Solte os SPEDs</li>
          <li><b>2</b>Confira o período</li>
          <li><b>3</b>Processe</li>
        </HowTo>
      </>
    );
  } else if (phase === "files") {
    const ignoredCount = plan.ignored.length;
    content = (
      <>
        {plan.companies.length > 0 ? (
          <Card>
            <Period>
              <div className="p">
                <div className="big">{periodText}</div>
                <div className="sub">
                  {formatCount(plan.companies.length)} {plan.companies.length === 1 ? "empresa" : "empresas"} · {formatCount(plan.usable.length)} {plan.usable.length === 1 ? "arquivo" : "arquivos"}
                </div>
              </div>
              {plan.missing.length === 0
                ? <span className="st"><i style={{ background: T.brand }} />Completo</span>
                : <span className="st"><i style={{ background: ORANGE }} />Falta {plan.missing.length}</span>}
            </Period>
          </Card>
        ) : (
          <Hint>{reading > 0 ? "Lendo os arquivos…" : "Nenhum arquivo válido ainda. Veja os motivos abaixo."}</Hint>
        )}

        {(offTarget || repeated.length > 0) && (
          <Hint style={{ margin: "10px 4px 0" }}>
            {offTarget && <>Há arquivos fora de <b>{formatMonthKey(targetMonth, true)}</b>; o período segue os arquivos. </>}
            {repeated.length > 0 && <>{repeated.map((m) => formatMonthKey(m)).join(", ")} já {repeated.length === 1 ? "tem" : "têm"} análise; esta será uma nova.</>}
          </Hint>
        )}

        {(plan.companies.length > 0 || reading > 0) && (
          <Section>
            <div className="lbl"><span>Empresas</span>{plan.months.length > 1 && <span>{plan.months.map(shortMonth).join(" · ")}</span>}</div>
            <Card>
              {plan.companies.map((c) => {
                const ids = [...c.files.values()].map((f) => f.id);
                return (
                  <Row key={c.cnpj}>
                    <span className="av">{branchNo(c.cnpj)}</span>
                    <span className="main">
                      <span className="t" title={c.company}>{branchLabel(c.cnpj)}</span>
                      <span className="s">{formatCnpj(c.cnpj)}</span>
                    </span>
                    <Months>
                      {plan.months.map((m) => (c.files.has(m)
                        ? <span key={m} className={targetMonth && m !== targetMonth ? "off" : ""} title={c.files.get(m).name}><FiCheck />{shortMonth(m)}</span>
                        : <span key={m} className="miss" title={`Falta o SPED de ${formatMonthKey(m, true)}`}>{shortMonth(m)}</span>))}
                    </Months>
                    <RoundButton type="button" className="x" onClick={() => removeIds(ids)} aria-label={`Remover ${branchLabel(c.cnpj)}`} title="Remover desta análise" style={{ width: 26, height: 26 }}>
                      <FiX />
                    </RoundButton>
                  </Row>
                );
              })}
              {reading > 0 && (
                <Row>
                  <span className="av"><Spinner /></span>
                  <span className="main"><span className="t">Lendo {reading} {reading === 1 ? "arquivo" : "arquivos"}…</span></span>
                </Row>
              )}
            </Card>
            {plan.missing.length > 0 && (
              <Hint style={{ margin: "8px 4px 0" }}>
                Envie o que falta ({plan.missing.slice(0, 3).map((x) => `${branchLabel(x.cnpj)}, ${shortMonth(x.month)}`).join("; ")}{plan.missing.length > 3 ? "…" : ""}) ou remova a empresa desta análise.
              </Hint>
            )}
          </Section>
        )}

        {ignoredCount > 0 && (
          <Section>
            <Card>
              <Disclosure type="button" $open={showIgnored} onClick={() => setShowIgnored((v) => !v)} aria-expanded={showIgnored}>
                <span className="dot" style={{ background: ORANGE }} />
                {ignoredCount} {ignoredCount === 1 ? "arquivo ignorado" : "arquivos ignorados"}
                <span className="n">· não entram na análise</span>
                <FiChevronDown className="chev" />
              </Disclosure>
              {showIgnored && plan.ignored.map((f) => (
                <Row key={f.id} $inset={16}>
                  <span className="main">
                    <span className="t" title={f.name}>{f.name}</span>
                    <span className="s">{f.dup ? `Repetido: ${branchLabel(f.cnpj)} em ${shortMonth(f.month)} já foi enviado` : f.reason}</span>
                  </span>
                  <RoundButton type="button" className="x" onClick={() => removeIds([f.id])} aria-label={`Remover ${f.name}`} title="Remover" style={{ width: 26, height: 26 }}>
                    <FiX />
                  </RoundButton>
                </Row>
              ))}
            </Card>
          </Section>
        )}

        <Section style={{ marginTop: 14 }}>
          <Drop $compact $over={over} {...dragHandlers}>
            {picker}
            <span className="plus">+</span>
            <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <span className="ct">{over ? "Solte para adicionar" : "Adicionar arquivos"}</span>
              <span className="cs">Arraste aqui ou clique</span>
            </span>
          </Drop>
        </Section>
      </>
    );
  } else {
    // ── Conteúdo: processamento ──
    let title = completion ? `Reprocessando ${completion.label}` : `Processando ${formatCount(plan.usable.length)} ${plan.usable.length === 1 ? "SPED" : "SPEDs"}`;
    let sub = current ? current.name : "Aguardando o servidor";
    if (runState === "done") { title = "Análise pronta"; sub = completion ? `${completion.label} · com os SPEDs enviados` : `${periodText} · ${formatCount(plan.companies.length)} empresas`; }
    if (runState === "error") { title = "O processamento parou"; sub = "Os arquivos continuam aqui para tentar de novo"; }
    content = (
      <>
        <Progress>
          <Ring value={runState === "done" ? 100 : overall} state={runState} />
          <div className="big">{title}</div>
          <div className="sub">{sub}</div>
        </Progress>
        <Section>
          <Card>
            {tasks.map((t) => (
              <Step key={t.id} $s={t.status}>
                <span className={`d ${t.status}`}>{t.status === "completed" ? <FiCheck /> : t.status === "error" ? <FiX /> : null}</span>
                <span className="nm">{t.name}</span>
                {t.status === "running" && t.progress > 0 && <span className="pc">{Math.round(t.progress)}%</span>}
              </Step>
            ))}
          </Card>
        </Section>
        <Section style={{ marginTop: 10 }}>
          <Card>
            <Disclosure type="button" $open={showLogs} onClick={() => setShowLogs((v) => !v)} aria-expanded={showLogs}>
              <span className="n" style={{ color: "inherit" }}>Detalhes técnicos</span>
              <FiChevronDown className="chev" />
            </Disclosure>
            {showLogs && (
              <Logs>
                {logs.map((l, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className={l.type === "error" ? "e" : ""}><span className="ts">{l.ts}</span>{l.message}</div>
                ))}
              </Logs>
            )}
          </Card>
        </Section>
      </>
    );
  }

  // ── Rodapé ──
  let msg = null;
  let actions = null;
  if (phase === "files" && completion) {
    const left = completion.missing.length - plan.usable.length;
    if (reading > 0) msg = <span>Lendo os arquivos…</span>;
    else if (plan.usable.length === 0) msg = <span>Nenhum SPED recebido</span>;
    else if (left > 0) msg = <><i style={{ background: ORANGE }} /><span>Ainda {left === 1 ? "falta" : "faltam"} {left} {left === 1 ? "SPED" : "SPEDs"}; pode processar assim mesmo</span></>;
    else msg = <><i style={{ background: T.brand }} /><span><b>Completo</b> · {completion.label}</span></>;
    actions = (
      <>
        <Capsule type="button" onClick={onClose}>Cancelar</Capsule>
        <PrimaryCapsule type="button" onClick={process} disabled={!canProcess}>Enviar e reprocessar</PrimaryCapsule>
      </>
    );
  } else if (phase === "files") {
    if (files.length === 0) msg = <span>Nenhum arquivo ainda</span>;
    else if (reading > 0) msg = <span>Lendo os arquivos…</span>;
    else if (plan.usable.length === 0) msg = <><i style={{ background: ORANGE }} /><span>Nenhum SPED válido</span></>;
    else if (plan.missing.length > 0) msg = <><i style={{ background: ORANGE }} /><span>Falta {plan.missing.length} {plan.missing.length === 1 ? "SPED" : "SPEDs"} para fechar o período</span></>;
    else msg = <><i style={{ background: T.brand }} /><span><b>Pronto</b> · {periodText}</span></>;
    actions = (
      <>
        <Capsule type="button" onClick={onClose}>Cancelar</Capsule>
        <PrimaryCapsule type="button" onClick={process} disabled={!canProcess}>Processar</PrimaryCapsule>
      </>
    );
  } else if (runState === "done") {
    msg = <><i style={{ background: T.brand }} /><span>A análise já está na lista</span></>;
    actions = (
      <>
        <Capsule type="button" onClick={onClose}>Fechar</Capsule>
        <PrimaryCapsule type="button" onClick={() => { onFinished?.(reportId); onClose(); }}>Ver análise</PrimaryCapsule>
      </>
    );
  } else if (runState === "error") {
    msg = <><i style={{ background: RED }} /><span>Falha no processamento</span></>;
    actions = (
      <>
        <Capsule type="button" onClick={onClose}>Fechar</Capsule>
        <PrimaryCapsule type="button" onClick={() => { setPhase("files"); setRunState("idle"); setJobId(null); }}>Tentar de novo</PrimaryCapsule>
      </>
    );
  } else {
    msg = <span>Pode fechar: continua em segundo plano</span>;
    actions = <Capsule type="button" onClick={onClose}>Fechar</Capsule>;
  }

  return createPortal(
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget && phase === "files" && files.length === 0) onClose(); }}>
      <Sheet role="dialog" aria-modal="true" aria-labelledby="nova-analise-title">
        <Header>
          <h2 id="nova-analise-title">{completion ? "Enviar SPEDs que faltam" : "Nova análise"}</h2>
          <RoundButton type="button" onClick={onClose} aria-label="Fechar" title="Fechar" style={{ width: 30, height: 30 }}><FiX /></RoundButton>
        </Header>
        <Body key={phase}>{content}</Body>
        <Foot>
          <div className="msg">{msg}</div>
          {actions}
        </Foot>
      </Sheet>
    </Overlay>,
    document.body
  );
}
